'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import SignUpForm from '@/components/forms/SignUpForm'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { NewsFavicon } from '@/components/NewsFavicon'
import { useExperiment } from '@/lib/experiments'

// Testimonial photos
import danImg from '@/images/testimonials/dan.jpg'
import danielImg from '@/images/testimonials/daniel.jpg'
import debImg from '@/images/testimonials/deb.jpg'
import ashlynImg from '@/images/testimonials/ashlyn.jpg'
import brianImg from '@/images/testimonials/brian.jpg'

const testimonialPhotos = [danImg, danielImg, debImg, ashlynImg, brianImg]

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL
const STORAGE_KEY = 'pano_modal_dismissed_at'
const HOURS_24 = 24 * 60 * 60 * 1000

const floatingFavicons = [
  { slug: 'cnn', alt: 'CNN', position: 'top-0 left-4', delay: '0s' },
  { slug: 'fox-news', alt: 'Fox News', position: 'top-8 right-0', delay: '0.5s' },
  { slug: 'reuters', alt: 'Reuters', position: 'top-1/4 -left-6', delay: '1s' },
  { slug: 'npr', alt: 'NPR', position: 'bottom-1/3 -right-4', delay: '0.3s' },
  { slug: 'the-daily-wire', alt: 'Daily Wire', position: 'bottom-12 left-0', delay: '0.7s' },
  { slug: 'axios', alt: 'Axios', position: 'top-1/3 -right-8', delay: '0.2s' },
  { slug: 'the-atlantic', alt: 'The Atlantic', position: '-top-4 right-1/4', delay: '0.8s' },
  { slug: 'politico', alt: 'Politico', position: 'bottom-4 right-8', delay: '0.4s' },
  { slug: 'cbs-news', alt: 'CBS News', position: 'bottom-0 -left-2', delay: '0.6s' },
  { slug: 'new-york-post', alt: 'NY Post', position: 'top-1/2 right-2', delay: '0.9s' },
]

export default function HomeModal({ topStories = [] }) {
  const [isVisible, setIsVisible] = useState(false)
  const ctaVariant = useExperiment('home-modal-cta')
  const buttonLabel = ctaVariant === 'v1' ? 'Subscribe' : 'Get 5 Free Issues'

  useEffect(() => {
    const dismissedAt = localStorage.getItem(STORAGE_KEY)
    if (dismissedAt) {
      const elapsed = Date.now() - parseInt(dismissedAt, 10)
      if (elapsed < HOURS_24) {
        return // Don't show modal, still within 24 hours
      }
    }
    setIsVisible(true)
  }, [])

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString())
    setIsVisible(false)
  }

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 z-50 bg-background">
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-6 right-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
      >
        <span className="group-hover:underline">Take me to the news</span>
        <XMarkIcon className="w-5 h-5" />
      </button>

      {/* Main content */}
      <div className="h-full flex items-center justify-center px-6">
        <div className="max-w-5xl w-full grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          {/* Left side - Tagline and form */}
          <div className="text-center md:text-left">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-serif text-foreground leading-tight mb-6">
              Get{' '}
              <span className="text-political-left">Both</span>{' '}
              <span className="text-political-right">Sides</span>
              <br />
              in 5 Minutes
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              A daily newsletter on how the Left, Right, and everything in between cover today's biggest stories.
            </p>
            <div className="max-w-sm mx-auto md:mx-0">
              <SignUpForm
                buttonLabel={buttonLabel}
                trackingLocation="home_modal"
              />
              <div className="mt-4 flex items-center justify-center md:justify-start gap-3">
                <div className="flex -space-x-2">
                  {testimonialPhotos.map((photo, index) => (
                    <Image
                      key={index}
                      src={photo}
                      alt=""
                      className="w-6 h-6 rounded-full border-2 border-background object-cover"
                    />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  Join 10,000+ readers
                </p>
              </div>
            </div>
          </div>

          {/* Right side - Newsletter mockup with floating logos */}
          <div className="hidden md:flex items-center justify-center">
            <div className="relative w-[420px] h-[480px]">
              {/* Floating news favicons */}
              {floatingFavicons.map((favicon) => (
                <div
                  key={favicon.alt}
                  className={`absolute ${favicon.position} z-10 animate-float`}
                  style={{ animationDelay: favicon.delay }}
                >
                  <NewsFavicon
                    src={`${SUPABASE_URL}/storage/v1/object/public/public_assets/news-favicons/${favicon.slug}.jpg`}
                    alt={favicon.alt}
                    faviconSize="xl"
                    className="shadow-lg ring-2 ring-white"
                  />
                </div>
              ))}

              {/* Story detail mockup */}
              {topStories[0] && (() => {
                const story = topStories[0]
                const articles = story.articles || []

                return (
                  <div className="absolute inset-10 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-100">
                      <Image
                        src="/logo.png"
                        alt="Pano News"
                        width={100}
                        height={39}
                        className="h-6 w-auto"
                      />
                    </div>

                    {/* Story content */}
                    <div className="p-6 space-y-5 h-full">
                      {/* Source count + Category */}
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="uppercase tracking-wide">{articles.length} Sources</span>
                        <span>•</span>
                        <span className="uppercase tracking-wide font-medium text-gray-700">{story.category || 'Politics'}</span>
                      </div>

                      {/* Headline */}
                      <h3 className="text-lg font-bold text-gray-900 leading-snug font-serif">
                        {story.title}
                      </h3>

                      {/* Three perspectives */}
                      <div className="grid grid-cols-3 gap-3 pt-3">
                        {/* Left */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full bg-blue-600"></div>
                            <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Left</span>
                          </div>
                          <div className="space-y-1.5">
                            <div className="h-2 bg-blue-100 rounded w-full"></div>
                            <div className="h-2 bg-blue-100 rounded w-5/6"></div>
                            <div className="h-2 bg-blue-100 rounded w-4/6"></div>
                            <div className="h-2 bg-blue-100 rounded w-full"></div>
                            <div className="h-2 bg-blue-100 rounded w-3/6"></div>
                          </div>
                        </div>

                        {/* Center */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full bg-gray-400"></div>
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Center</span>
                          </div>
                          <div className="space-y-1.5">
                            <div className="h-2 bg-gray-200 rounded w-full"></div>
                            <div className="h-2 bg-gray-200 rounded w-4/6"></div>
                            <div className="h-2 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-2 bg-gray-200 rounded w-full"></div>
                            <div className="h-2 bg-gray-200 rounded w-4/6"></div>
                          </div>
                        </div>

                        {/* Right */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full bg-red-600"></div>
                            <span className="text-xs font-semibold text-red-600 uppercase tracking-wide">Right</span>
                          </div>
                          <div className="space-y-1.5">
                            <div className="h-2 bg-red-100 rounded w-full"></div>
                            <div className="h-2 bg-red-100 rounded w-3/6"></div>
                            <div className="h-2 bg-red-100 rounded w-5/6"></div>
                            <div className="h-2 bg-red-100 rounded w-full"></div>
                            <div className="h-2 bg-red-100 rounded w-5/6"></div>
                          </div>
                        </div>
                      </div>

                      {/* Bottom section - sources by perspective (deduplicated) */}
                      <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-100">
                        {['left', 'center', 'right'].map((bias) => {
                          const seen = new Set()
                          const biasArticles = articles.filter(a => {
                            const b = a.outlet?.bias ?? 0
                            const matches = bias === 'left' ? b < -6 : bias === 'right' ? b > 6 : b >= -6 && b <= 6
                            if (!matches || seen.has(a.outlet?.slug)) return false
                            seen.add(a.outlet?.slug)
                            return true
                          }).slice(0, 3)

                          return (
                            <div key={bias} className="flex -space-x-1.5">
                              {biasArticles.map((article, i) => (
                                <NewsFavicon
                                  key={i}
                                  src={`${SUPABASE_URL}/storage/v1/object/public/public_assets/news-favicons/${article.outlet?.slug}.jpg`}
                                  alt={article.outlet?.name}
                                  faviconSize="sm"
                                  className="ring-2 ring-white"
                                />
                              ))}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
